import React from 'react';
import { ChevronUpIcon, EllipsisIcon, PencilIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import { useNotifications } from '~/modules/notifications';
import { useChat } from '../../hooks';
import {
    CHAT_BOX_CLOSED_WIDTH_REM,
    DRAWER_MARGIN_REM,
    DRAWER_WIDTH_REM,
} from '../../utils';
import { NotificationBadge } from '../NotificationBadge';

/**
 * The floating chat inbox bubble which opens the chat inbox drawer.
 * @see {@link ChatInboxDrawer}
 */
export const ChatInboxFloatingButton = React.forwardRef<
    HTMLDivElement,
    React.ComponentPropsWithoutRef<'div'>
>(function ChatInboxFloatingButton({ className, ...divProps }, ref) {
    const { setChatInboxState, openChatBox } = useChat();
    const { notifications } = useNotifications();

    return (
        <div
            ref={ref}
            className={cn(
                'fixed bottom-4 z-fixed flex items-center gap-1 rounded-full bg-white/60 px-5 py-2 text-black shadow-lg backdrop-blur-xl hover:bg-white/80',
                // Prevent the floating button from shifting when the scrollbar is locked.
                '[body[data-scroll-locked="1"]_&]:translate-x-[calc(0px-var(--removed-body-scroll-bar-size,14px))]',
                className
            )}
            style={{
                height: `${CHAT_BOX_CLOSED_WIDTH_REM}rem`,
                width: `${DRAWER_WIDTH_REM}rem`,
                right: `${DRAWER_MARGIN_REM}rem`,
            }}
            role="button"
            aria-label="My Messages"
            {...divProps}
        >
            <NotificationBadge count={notifications.userInbox?.count} />
            <div className="ml-2 min-w-0 flex-1 truncate text-4.5 leading-tight">
                {notifications.userInbox?.count
                    ? 'Unread Messages'
                    : 'My Messages'}
            </div>
            <Popover>
                <PopoverTrigger
                    className="flex size-10 items-center justify-center rounded-full hover:bg-black/10"
                    onClick={(e) => e.stopPropagation()}
                >
                    <EllipsisIcon className="size-6" />
                </PopoverTrigger>
                <PopoverContent
                    className="min-w-32 overflow-hidden rounded-lg bg-gray-100 p-0 before:rounded-lg"
                    side="top"
                    sideOffset={12}
                    onClick={(e) => e.stopPropagation()}
                >
                    <ul className="flex flex-col text-4 leading-tight tracking-wide">
                        <li
                            className="p-2 hover:bg-gray-200"
                            role="button"
                            onClick={() => setChatInboxState('hidden')}
                        >
                            Hide
                        </li>
                    </ul>
                </PopoverContent>
            </Popover>
            <button
                className="flex size-10 items-center justify-center rounded-full hover:bg-black/10"
                aria-label="New Message"
                onClick={(e) => {
                    e.stopPropagation();
                    setChatInboxState('open');
                    openChatBox(0);
                }}
            >
                <PencilIcon className="size-5.5" />
            </button>
            <ChevronUpIcon className="size-7" />
        </div>
    );
});
